/* ──────────────────────────────────────────────────────────────
   src/hooks/useCalendario.ts   (versión con includePendientes)
   ────────────────────────────────────────────────────────────── */
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import api from "../api/axios";
import type { MotivoSlug } from "../constants/enums";

export interface EventoCalendario {
  tipo: "vacacion" | "vacacion_pendiente" | "ausencia";
  fecha: string;
  allDay: boolean;
  ausencia?: {
    id: number;
    estado: "pending" | "approved" | "rejected";
    slug: MotivoSlug;
    colorHex: string;
    justificada: boolean;
  };
  solicitudId?: number;
}

/* ———————————————————————————————————————————————— */
export function useMotivosAusencia() {
  return useQuery({
    queryKey: ["motivos_ausencia"],
    queryFn: () => api.get("/motivos-ausencia").then((r) => r.data),
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * @param idProfesor          ID del profesor
 * @param year                Año de inicio de curso (2024 ⇒ 2024‑09‑01 … 2025‑08‑31)
 * @param opts.includePendientes  ¿Incluir solicitudes VAC pendientes? (solo HR)
 */
export function useCalendario(
  idProfesor: number,
  year: number,
  opts: { includePendientes?: boolean } = {}
) {
  const { includePendientes = true } = opts;

  /* rango curso */
  const startDate = `${year}-09-01`;
  const endDate = dayjs(startDate).add(1, "year").subtract(1, "day").format("YYYY-MM-DD");

  return useQuery<EventoCalendario[]>({
    queryKey: ["calendario", idProfesor, startDate, endDate, includePendientes],
    enabled: !!idProfesor,
    queryFn: async () => {
      /* 1. Vacaciones confirmadas */
      const vacaciones = await api
        .get<{ id: number; fecha: string }[]>("/vacaciones", {
          params: { profesor: idProfesor, start: startDate, end: endDate },
        })
        .then((r) =>
          r.data.map((v) => ({
            tipo: "vacacion" as const,
            fecha: v.fecha,
            allDay: true,
          }))
        );

      /* 2. Ausencias (GET visible para todos los roles) */
      const ausencias = await api
        .get<any[]>("/ausencias", {
          params: { profesor: idProfesor, start: startDate, end: endDate },
        })
        .then((r) =>
          r.data.map((a) => ({
            tipo: "ausencia" as const,
            fecha: a.fecha,
            allDay: a.horaInicio == null && a.horaFin == null,
            ausencia: {
              id: a.id,
              estado: a.estado,
              slug: a.motivo.slug as MotivoSlug,
              colorHex: a.motivo.colorHex,
              justificada: a.tipo === "justificada",
            },
          }))
        );

      /* 3. Solicitudes VAC pendientes — solo si está permitido */
      let solicitudes: EventoCalendario[] = [];
      if (includePendientes) {
        try {
          solicitudes = await api
            .get<{ id: number; fecha: string }[]>("/solicitudes-vacaciones", {
              params: { estado: "pendiente", profId: idProfesor },
              headers: { "x-silent-403": true }, // que no muestre toast
            })
            .then((r) =>
              r.data.map((s) => ({
                tipo: "vacacion_pendiente" as const,
                fecha: s.fecha,
                allDay: true,
                solicitudId: s.id,
              }))
            );
        } catch (err: any) {
          /* Si el backend devuelve 403 lo ignoramos y seguimos */
          if (err?.response?.status !== 403) throw err;
        }
      }

      return [...vacaciones, ...ausencias, ...solicitudes];
    },
  });
}
